// src/views/admin/creditNotes/index.jsx
import { useToast, Button } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import InvoicesTable from '../../../components/Invoices/InvoiceTable.js';
import CreditNoteFilters from '../../../components/CreditNotes/CreditNoteFilters.js';
import { columnsDataCreditNotes } from './variables/columnsDataCreditNotes.js';
import React, { useEffect, useRef, useState } from 'react';
import { getData, postData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';

export default function CreditNotes() {
  const { resetListFilters } = useSearch(); 
  const [data, setData] = useState([]); 
  const [page, setPage] = useState(1); // Cambiar a 1-based para el backend
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0); 
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const rowsPerPage = 10; 
  const toast = useToast();
  const skipSortReloadRef = useRef(true);
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  const loadData = async (filters = {}, pageNum = 1, skipSync = false) => {
    try {
      setIsLoading(true);
      
      console.log('Loading credit notes with filters:', filters, 'page:', pageNum);
      
      // Sincronización inteligente automática (solo si no se está saltando)
      if (!skipSync) {
        try {
          console.log('Starting automatic smart sync for credit notes...');
          const syncResponse = await postData('/invoices/sync/credit-notes', {});
          
          if (syncResponse.status === 200) {
            const { synced, skipped, isFirstSync } = syncResponse.payload;
            console.log(`Auto-sync completed: ${synced} synced, ${skipped} skipped, first sync: ${isFirstSync}`);
            
            // Solo mostrar notificación si se sincronizaron notas de crédito
            if (synced > 0) {
              toast({
                title: isFirstSync ? 'Sincronización inicial completada' : 'Nuevas notas de crédito sincronizadas',
                description: `${synced} documentos sincronizados${skipped > 0 ? `, ${skipped} ya existían` : ''}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          }
        } catch (syncError) {
          console.warn('Auto-sync failed, continuing with local data:', syncError);
          // No mostrar error de sincronización al usuario, continuar con datos locales
        }
      }
      
      // SIEMPRE usar el endpoint de búsqueda local para mejor rendimiento
      console.log('Using local credit notes search endpoint');
      const queryParams = new URLSearchParams({
        page: pageNum.toString(),
        limit: rowsPerPage.toString(),
        ...filters
      });
      appendSortParams(queryParams);

      console.log('Using local search endpoint with params:', queryParams.toString());
      const response = await getData(`/invoices/search/credit-notes/local?${queryParams}`);
      console.log('Local credit notes search response:', response);
      
      const result = response?.payload;
      
      // Formatear los datos para mostrar "documento - cliente"
      const formattedData = (result?.creditNotes || []).map(note => ({
        ...note,
        customer: note.customer ? {
          ...note.customer,
          displayName: `${note.customer.documentNumber} - ${note.customer.name}`
        } : null
      }));
      
      setData(formattedData);
      setTotal(result?.total || 0);
      setTotalPages(result?.totalPages || 0);
      
    } catch (error) {
      console.error('Error loading credit notes:', error);
      toast({
        title: 'Error al obtener notas de crédito',
        description: error.message || 'No se pudieron cargar las notas de crédito',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (filters) => {
    setCurrentFilters(filters);
    setPage(1); // Resetear a la primera página
    loadData(filters, 1, true); // Saltar sync en búsquedas porque ya se sincronizó al cargar
  };

  const handlePageChange = (newPage) => {
    setPage(newPage + 1); // Convertir de 0-based a 1-based
    loadData(currentFilters, newPage + 1, true); // Saltar sync en cambios de página
  };

  const handleManualSync = async () => {
    if (isSyncing) return;

    try {
      setIsSyncing(true);
      const syncResponse = await postData('/invoices/sync/credit-notes', {});

      if (syncResponse.status === 200) {
        const { synced, skipped, isFirstSync } = syncResponse.payload;
        if (synced > 0) {
          toast({
            title: isFirstSync
              ? 'Sincronización inicial completada'
              : 'Nuevas notas de crédito sincronizadas',
            description: `${synced} documentos sincronizados${skipped > 0 ? `, ${skipped} ya existían` : ''}`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        await loadData(currentFilters, page, true);
      }
    } catch (error) {
      toast({
        title: 'Error al sincronizar',
        description: error.message || 'No se pudieron sincronizar las notas de crédito.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePrintCreditNote = async (action, rowData) => {
    if (action.type === 'print') {
      try {
        const response = await getData(`/invoices/document/${rowData.id}`);

        const base64Data = response.payload.document.data;
        const mimeType = response.payload.document.mime_type;

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } catch (error) {
        toast({
          title: 'Error al abrir el PDF de la nota de crédito',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    loadData({}, 1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortDir]);

  useEffect(() => {
    if (skipSortReloadRef.current) {
      skipSortReloadRef.current = false;
      return;
    }
    loadData(currentFilters, 1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDir]);

  return (
    <AdminPageShell
      title="Notas de crédito"
      actions={
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={handleManualSync}
          isLoading={isSyncing}
          loadingText="Sincronizando"
        >
          Sincronizar notas de crédito
        </Button>
      }
    >
      <CreditNoteFilters onSearch={handleSearch} isLoading={isLoading} />
      <InvoicesTable
        columnsData={columnsDataCreditNotes}
        data={data}
        page={page - 1}
        setPage={handlePageChange}
        totalPages={totalPages}
        handlePrintInvoice={handlePrintCreditNote}
        isLoading={isLoading}
        total={total}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </AdminPageShell>
  );
}
